import { useEffect, useState } from "react";
import { Cloud, Download, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Server-side cloud backup + restore. Complements the IndexedDB local
 * auto-backup that runs every 24h. We keep up to 5 snapshots per profile
 * server-side; older ones are pruned automatically.
 */
export default function CloudBackup({ profileId }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [label, setLabel] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/cloud-backups?profile_id=${profileId}`);
      setBackups(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const create = async () => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const url = `/profiles/${profileId}/cloud-backup${label.trim() ? `?label=${encodeURIComponent(label.trim())}` : ""}`;
      const { data } = await api.post(url);
      setBackups((arr) => [data, ...arr].slice(0, 5));
      setInfo(`Snapshot saved · ${data.tile_count} tiles · ${(data.bytes / 1024).toFixed(1)} KB`);
      setLabel("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const restore = async (id) => {
    if (!window.confirm("Restore this backup? It will replace your current categories and tiles on this board.")) return;
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const { data } = await api.post(`/cloud-backups/${id}/restore`);
      setInfo(`Restored · ${data.categories_restored} categories · ${data.tiles_restored} tiles. Reload the board to see the change.`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this backup? This cannot be undone.")) return;
    try {
      await api.delete(`/cloud-backups/${id}`);
      setBackups((arr) => arr.filter((b) => b.id !== id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <section className="brutal-card bg-white p-5 mb-4" data-testid="cloud-backup-section">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-9 h-9 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center">
          <Cloud size={16} />
        </span>
        <div>
          <h2 className="display text-xl font-bold leading-tight">Cloud backup &amp; restore</h2>
          <p className="text-xs text-[color:var(--ink-soft)]">
            Snapshots live on our server (last 5 kept). Use this before big changes you might want to undo.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="text"
          maxLength={80}
          className="brutal-input flex-1 min-w-[12rem]"
          placeholder="Label (optional, e.g. 'before potty-training tiles')"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="cloud-backup-label"
        />
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="pill-btn primary"
          data-testid="cloud-backup-create"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy ? "Saving…" : "Back up now"}
        </button>
      </div>

      {info && <div className="brutal-card bg-[#E6F8E1] p-2 text-sm mb-2" data-testid="cloud-backup-info">{info}</div>}
      {error && <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 text-sm mb-2" data-testid="cloud-backup-error">{error}</div>}

      {loading ? (
        <div className="text-[color:var(--ink-soft)] text-sm">Loading backups…</div>
      ) : backups.length === 0 ? (
        <div className="text-[color:var(--ink-soft)] text-sm">No cloud backups yet. Create your first one above.</div>
      ) : (
        <ul className="space-y-2" data-testid="cloud-backup-list">
          {backups.map((b) => {
            const ts = new Date(b.created_at);
            return (
              <li
                key={b.id}
                className="brutal-card bg-[color:var(--paper-muted)] p-3 flex flex-wrap items-center gap-2"
                data-testid={`cloud-backup-row-${b.id}`}
              >
                <div className="flex-1 min-w-[10rem]">
                  <div className="display font-bold text-sm">
                    {b.label || ts.toLocaleString()}
                  </div>
                  <div className="text-xs text-[color:var(--ink-soft)]">
                    {b.tile_count} tiles · {b.category_count} categories · {(b.bytes / 1024).toFixed(1)} KB
                    {b.label ? ` · ${ts.toLocaleString()}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => restore(b.id)}
                  disabled={busy}
                  className="pill-btn"
                  data-testid={`cloud-backup-restore-${b.id}`}
                >
                  <RotateCcw size={13} /> Restore
                </button>
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  disabled={busy}
                  className="pill-btn danger"
                  data-testid={`cloud-backup-delete-${b.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
